import React, { useState, useEffect, useRef } from 'react';
import { appOperations } from '../../../App/store';
import { connect } from 'react-redux';
import CloseIcon from '@mui/icons-material/CloseRounded';
import { exportsActions } from '../../pages/ExportsPage/store';
import { Dialog, DialogActions, Button, Grid, Select, FormControl, IconButton } from '@mui/material';
import { DialogContent, FormControlLabel, Checkbox, Typography, TextField } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import DialogTitle from '@mui/material/DialogTitle';
import DialogCloseButton from '../../buttons/DialogCloseButton';
import ColoredSnackbarConstants from '../../misc/ColoredSnackbar/ColoredSnackbarConstants';
import CircleProgressButton from '../../buttons/CircleProgressButton';
import axios from 'axios';

const styles = () => ({
    checkBox: {
        width: "100%",
        marginTop: "10px"
    },
    input: {
        marginButton: "15px"
    },
    fileInput: {
        display: 'none'
    },
    thumbnailFileName: {
        fontSize: '16px',
        margin: '10px 5px',
        wordBreak: 'break-all'
    },
    closeButton: {
        margin: '0 5px',
    },
    uploadedImage: {
        maxHeight: '100px',
    },
    imagePreview: {
        maxHeight: '200px',
        maxWidth: '100%',
        margin: '20px 0'
    }
});

const newPresetName = "New Preset";

const ManageVideoPresetDialog = ({ classes, manageVideoPreset, openSnack, isVideoPresetCreating, open, onClose }) => {
    const [isEnabled, setIsEnabled] = useState(false);
    const [presets, setPresets] = useState([]);
    const [selectedPreset, setSelectedPreset] = useState(null);
    const [isPremium, setIsPremium] = useState(false);
    const [thumbnailBlobUrl, setThumbnailBlobUrl] = useState(null);
    const [thumbnailFileName, setThumbnailFileName] = useState(null);
    const [presetName, setPresetName] = useState("");
    const thumbnailFileRef = useRef(null);

    useEffect(() => {
        open && fetchPresets();
    }, [open])

    const fetchPresets = async () => {
        try {
            const response = await axios.get('api/VideoPresets/GetPresets');
            const presets = response.data.presets;
            const presetList = presets.map(element => ({
                id: element.id,
                name: element.name,
                enabled: element.enabled,
                isPremium: element.isPremium,
                thumbnailUrl: element.thumbnailUrl
            }));

            presetList.unshift({ id: "", name: newPresetName });
            setSelectedPreset({ id: "", name: newPresetName });
            setPresets(presetList);
        } catch (error) {
            console.error("Error fetching presets:", error);
        }
    };

    const onPresetSelect = (presetId) => {
        const preset = presets.find(x => x.id === presetId);

        if (preset.name !== newPresetName) {
            const { enabled, isPremium, name } = preset;
            setSelectedPreset(preset);
            setIsEnabled(enabled);
            setIsPremium(isPremium);
            setPresetName(name);
        } else {
            setSelectedPreset(null);
            setIsEnabled(false);
            setIsPremium(false);
            setPresetName("");
        }
    }

    const onPresetNameChange = (presetName) => {
        setPresetName(presetName);

        const preset = presets.find(x => x.name === presetName);
        if (preset) {
            setSelectedPreset(preset);
        }
    };

    const onSubmitClick = () => {
        const thumbnail = thumbnailFileRef.current.files[0];

        if (!presetName) {
            openSnack("Preset name is missing.", ColoredSnackbarConstants.SNACKBAR_ERROR);
            return;
        }

        if (!thumbnail && selectedPreset?.name === newPresetName) {
            openSnack("Thumbnail is missing.", ColoredSnackbarConstants.SNACKBAR_ERROR);
            return;
        }

        manageVideoPreset(presetName.trim(), thumbnail, isEnabled, isPremium, selectedPreset?.id);
    }

    const onThumbnailChange = (file) => {
        const blobUrl = URL.createObjectURL(file);
        setThumbnailBlobUrl(blobUrl);
        setThumbnailFileName(file.name);
    }

    const onUploadThumbnailClick = () => {
        thumbnailFileRef.current.click();
    }

    const onDialogClose = () => {
        setIsEnabled(false);
        setIsPremium(false);
        setPresetName("");
        onThumbnailFileRemove();
        onClose();
    }

    const onThumbnailFileRemove = () => {
        thumbnailFileRef.current.value = '';
        setThumbnailBlobUrl(null);
        setThumbnailFileName(null);
    }

    return (
        <Dialog
            onClose={onDialogClose}
            maxWidth='sm'
            fullWidth={true}
            open={open}
        >
            <DialogTitle id='recaptcha-dialog-title'>
                Manage preset
                <DialogCloseButton onClick={onDialogClose} />
            </DialogTitle>
            <input
                type='file'
                accept="image/*"
                id="thumbnail-input"
                className={classes.fileInput}
                onChange={(e) => onThumbnailChange(e.target.files[0])}
                ref={thumbnailFileRef}
            />
            <DialogContent style={{ display: 'block' }}>
                <Grid
                    container
                    direction='column'
                    alignItems='center'
                >
                    <Grid>
                        {selectedPreset && selectedPreset.name !== newPresetName && <img className={classes.imagePreview} src={selectedPreset.thumbnailUrl} />}
                    </Grid>
                    <Grid />
                    <FormControl variant="standard" fullWidth>
                        <Select
                            variant="standard"
                            native
                            value={selectedPreset?.id}
                            onChange={(event) => onPresetSelect(event.target.value)}>
                            {presets?.map((preset) => <option key={preset?.id} value={preset?.id}>{preset?.name}</option>)}
                        </Select>
                    </FormControl>

                    <TextField
                        variant="standard"
                        value={presetName}
                        disabled={isVideoPresetCreating}
                        onChange={(e) => onPresetNameChange(e.target.value)}
                        className={classes.input}
                        autoFocus
                        margin="dense"
                        id="presetName"
                        label="Preset Name"
                        type="text"
                        fullWidth
                    />
                    <Grid />
                    <Grid sx={{ width: "100%" }} container spacing={2} alignItems="center">
                        <Grid size={{ sm: 2, xs: 8, md: 2 }}>
                            <FormControlLabel
                                className={classes.checkBox}
                                disabled={isVideoPresetCreating}
                                control={
                                    <Checkbox
                                        checked={isEnabled}
                                        color="secondary"
                                        onChange={(e) => setIsEnabled(e.target.checked)}
                                    />
                                }
                                label={"Enabled"}
                            />
                        </Grid>
                        <Grid />
                        <Grid size={{ md: 2, sm: 2, xs: 8 }}>
                            <FormControlLabel
                                className={classes.checkBox}
                                disabled={isVideoPresetCreating}
                                control={
                                    <Checkbox
                                        checked={isPremium}
                                        color="secondary"
                                        onChange={(e) => setIsPremium(e.target.checked)}
                                    />
                                }
                                label={"Pro"}
                            />
                        </Grid>
                        <Grid size={{ md: 12 }} container direction="column" justifyContent="flex-start">
                            {
                                thumbnailBlobUrl &&
                                <Grid>
                                    <img className={classes.uploadedImage} src={thumbnailBlobUrl} />
                                </Grid>
                            }
                            {thumbnailFileName &&
                                <Grid>
                                    <Typography
                                        variant='body2'
                                        color='textSecondary'
                                        className={classes.thumbnailFileName}
                                    >
                                        {thumbnailFileName}
                                    </Typography>
                                </Grid>
                            }
                            <Grid>
                                <Grid container alignItems="center">
                                    <Grid>
                                        <CircleProgressButton
                                            variant='outlined'
                                            color="inherit"
                                            size='small'
                                            onClick={onUploadThumbnailClick}
                                            component='span'
                                        >
                                            {thumbnailFileName ? "Change Thumbnail" : "Upload Thumbnail"}
                                        </CircleProgressButton>
                                    </Grid>
                                    {thumbnailFileName &&
                                        <Grid>
                                            <IconButton
                                                className={classes.closeButton}
                                                aria-label='close'
                                                onClick={onThumbnailFileRemove}
                                                size="large">
                                                <CloseIcon />
                                            </IconButton>
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onDialogClose} color="primary">
                    Cancel
                </Button>
                <CircleProgressButton
                    color="primary"
                    className={classes.submit}
                    onClick={onSubmitClick}
                    loading={isVideoPresetCreating}
                >
                    Save
                </CircleProgressButton>
            </DialogActions>
        </Dialog>
    );
}

const mapDispatchToProps = (dispatch) => {
    const manageVideoPreset = (presetName, file, isEnabled, isPremium, selectedPresetId) => dispatch(exportsActions.manageVideoPreset(presetName, file, isEnabled, isPremium, selectedPresetId));
    const openSnack = (text, variant) => dispatch(appOperations.openSnack(text, variant));

    return {
        manageVideoPreset,
        openSnack
    };
};

const mapStateToProps = (state) => {
    const { isVideoPresetCreating } = state.exports;

    return {
        isVideoPresetCreating
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(ManageVideoPresetDialog, styles));
